import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';

import { ApContentService } from '@ap/content';
import { ErrorMessages } from '@ap/form/errors';

import { nameAndDevicePattern } from '../tokens-browser.constants';
import { TokensBrowserService } from '../tokens-browser.service';

import content from './name.content.json';
import { NameValidator } from './name.validator';

/**
 * @module NameModule
 * @ngdoc component
 * @name NameComponent
 */
@Component({
    selector: 'tokens-browser-name',
    templateUrl: 'name.component.html',
    standalone: false
})
export class NameComponent implements OnInit {
    @Input()
    public name: string;

    @Output()
    public newName: EventEmitter<string> = new EventEmitter();

    @Output()
    public back: EventEmitter<string> = new EventEmitter();

    public form: UntypedFormGroup;
    public buttonsDisabled: boolean;

    public errorMessage: ErrorMessages;

    private _registeredNames: string[];

    public constructor(
        private readonly contentService: ApContentService,
        private readonly tokensBrowserService: TokensBrowserService
    ) {}

    public get registeredNames(): string[] {
        return this._registeredNames;
    }

    @Input()
    public set registeredNames(registeredNames: string[]) {
        if (registeredNames) {
            this._registeredNames = registeredNames;
            this.setFormValidators();
        }
    }

    public ngOnInit(): void {
        this.contentService.addContentItems(content);
        this.form = new UntypedFormGroup({
            name: new UntypedFormControl(this.name)
        });
        this.setFormValidators();
        this.tokensBrowserService.setFocusOnActiveInput('tokens-browser-name ap-input input');

        this.errorMessage = {
            required: this.contentService.get(`tokens.browser.name.error.required`),
            maxlength: this.contentService.get(`tokens.browser.name.error.maxlength`),
            alreadyExists: this.contentService.get(`tokens.browser.name.error.alreadyExists`),
            pattern: this.contentService.get(`tokens.browser.name.error.pattern`)
        };
    }

    public submit(): void {
        this.buttonsDisabled = true;

        if (this.form.valid) {
            this.newName.emit(this.form.get('name').value);
        } else {
            this.buttonsDisabled = false;
            Object.keys(this.form.controls).forEach((field) => {
                const control = this.form.get(field);
                control.markAsTouched();
                control.markAsDirty();
            });
        }
    }

    public goBack(): void {
        this.back.emit(this.form.get('name').value);
    }

    public onBlur(): void {
        let formattedValue = this.form.get('name').value;
        formattedValue = this.tokensBrowserService.formatSpacing(formattedValue);
        this.form.get('name').setValue(formattedValue);
    }

    public getAriaLabel(): string {
        const title = this.contentService.get('tokens.browser.name.title');
        const info = this.contentService.get('tokens.browser.name.info');
        return `${title}, ${info}`;
    }

    private setFormValidators(): void {
        if (this.form && this.form.get('name')) {
            this.form
                .get('name')
                .setValidators([
                    Validators.required,
                    Validators.maxLength(100),
                    Validators.pattern(nameAndDevicePattern),
                    NameValidator.alreadyExists(this.registeredNames)
                ]);
            this.form.get('name').updateValueAndValidity();
        }
    }
}
